/**
 * Cartão "Situação da NF-e" (resultado da Venda Direta e da Venda na Rota): em que ponto está
 * a emissão (etapas reais, sem porcentagem inventada), os dados da nota e, depois da
 * autorização, os arquivos (links diretos) e a impressão. A situação vem do sistema (gravada
 * pelo worker); este cartão só lê e mostra.
 */
import { FISCAL_ENVIRONMENT_LABEL, type FiscalDocumentDetail } from "@cafegestor/shared";
import { Icon, type IconName } from "@/components/icons";
import { Alert, Button, StatusBadge } from "@/components/ui";
import { errorMessage } from "@/lib/api";
import { dateTimeBR } from "../estoque/lib";
import { DocStatusBadge } from "./DocumentDetail";
import { filesOf, type DeliveryState } from "./emission-actions";
import type { EmissionStage, TimelineStep } from "./emission-lib";
import { FiscalFileLinks } from "./FiscalFileLinks";
import { PrintActions } from "./PrintActions";
import { useFiscalAction } from "./lib";

const STAGE_ICON: Record<EmissionStage["tone"], IconName> = {
  ok: "check",
  error: "alert",
  warn: "alert",
  info: "info",
  neutral: "info",
};
const STAGE_BOX: Record<EmissionStage["tone"], string> = {
  ok: "border-success/30 bg-success-soft text-success-strong",
  error: "border-destructive/30 bg-destructive-soft text-destructive",
  warn: "border-warning/30 bg-warning-soft text-warning-strong",
  info: "border-info/25 bg-info-soft text-info",
  neutral: "border-border bg-muted text-secondary-foreground",
};

const STEP_DOT: Record<TimelineStep["state"], string> = {
  done: "border-success bg-success text-white",
  current: "border-caramel text-caramel",
  pending: "border-border text-transparent",
  warn: "border-warning text-warning-strong",
  error: "border-destructive text-destructive",
};
const STEP_TEXT: Record<TimelineStep["state"], string> = {
  done: "concluída",
  current: "em andamento",
  pending: "a seguir",
  warn: "atenção",
  error: "não concluída",
};

/** Etapas reais da emissão: na vertical no celular, em linha no computador. */
export function EmissionTimeline({ steps }: { steps: TimelineStep[] }) {
  return (
    <ol
      aria-label="Etapas da emissão"
      className="@container grid gap-2 sm:grid-cols-3 lg:grid-cols-6 lg:gap-1"
    >
      {steps.map((s) => (
        <li
          key={s.key}
          data-state={s.state}
          className={`flex items-center gap-2.5 text-sm lg:flex-col lg:items-start lg:gap-1.5 ${
            s.state === "pending" ? "text-muted-foreground" : "text-foreground"
          } ${s.state === "current" || s.state === "error" || s.state === "warn" ? "font-semibold" : ""}`}
        >
          <span
            aria-hidden
            className={`flex size-6 shrink-0 items-center justify-center rounded-full border-2 text-[13px] font-bold ${STEP_DOT[s.state]}`}
          >
            {s.state === "done" ? (
              <Icon name="check" size={14} strokeWidth={3} />
            ) : s.state === "error" || s.state === "warn" ? (
              "!"
            ) : s.state === "current" ? (
              <span className="block size-2 animate-pulse rounded-full bg-current" />
            ) : null}
          </span>
          <span className="leading-snug">
            {s.label}
            <span className="sr-only"> ({STEP_TEXT[s.state]})</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

export function EmissionCard({
  doc,
  stage,
  steps,
  delivery,
}: {
  doc: FiscalDocumentDetail;
  stage: EmissionStage;
  /** etapas reais (emission-lib/emissionTimeline) */
  steps: TimelineStep[];
  delivery: DeliveryState;
}) {
  const nfe = doc.model === "65" ? "NFC-e" : "NF-e";
  const consult = useFiscalAction<Record<string, never>>(() => `/documentos/${doc.id}/consultar`);
  const { danfe } = filesOf(doc);
  const authorized = doc.status === "autorizada";

  const facts: { label: string; value: string; mono?: boolean; wide?: boolean }[] = [
    { label: "Modelo", value: `${doc.model} (${nfe})` },
    { label: "Série", value: String(doc.series) },
    { label: "Número", value: String(doc.number) },
    { label: "Ambiente fiscal", value: FISCAL_ENVIRONMENT_LABEL[doc.environment] },
    { label: "Protocolo", value: doc.protocol ?? "—", mono: true },
    { label: "Autorização", value: dateTimeBR(doc.authorizedAt) },
    { label: "Chave de acesso", value: doc.accessKey ?? "—", mono: true, wide: true },
  ];

  return (
    <section aria-labelledby="situacao-nfe" className="rounded-xl border bg-card shadow-card">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border/70 px-5 py-4">
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="flex size-10 items-center justify-center rounded-full bg-caramel-soft text-accent"
          >
            <Icon name="fiscal" size={20} />
          </span>
          <div>
            <h2 id="situacao-nfe" className="text-[17px] font-semibold">
              Situação da {nfe}
            </h2>
            <p className="text-sm text-muted-foreground">
              Série {doc.series} · nº {doc.number}
            </p>
          </div>
        </div>
        <DocStatusBadge status={doc.status} />
      </header>

      <div className="space-y-4 p-5">
        {doc.environment !== "producao" && (
          <p className="flex items-center gap-2 rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-sm font-medium text-warning-strong">
            <Icon name="alert" size={16} />
            {doc.environment === "homologacao"
              ? "Documento emitido em ambiente de homologação — sem valor fiscal."
              : "Documento do simulador — sem valor fiscal e sem envio à SEFAZ."}
          </p>
        )}

        <div
          role="status"
          aria-live="polite"
          className={`flex gap-3 rounded-lg border px-4 py-3 ${STAGE_BOX[stage.tone]}`}
        >
          <span aria-hidden className="mt-0.5 shrink-0">
            {stage.waiting && stage.tone === "info" ? (
              <span className="block size-5 animate-spin rounded-full border-2 border-current/25 border-t-current" />
            ) : (
              <Icon name={STAGE_ICON[stage.tone]} size={20} />
            )}
          </span>
          <div className="min-w-0">
            <p className="font-semibold">{stage.title}</p>
            <p className="mt-0.5 text-sm leading-relaxed opacity-90">{stage.detail}</p>
          </div>
        </div>

        <div className="space-y-1.5">
          <EmissionTimeline steps={steps} />
          {stage.waiting && <p className="text-[13px] text-muted-foreground">Atualiza sozinha.</p>}
        </div>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg bg-background/70 px-4 py-3 sm:grid-cols-3">
          {facts.map((f) => (
            <div key={f.label} className={f.wide ? "col-span-2 min-w-0 sm:col-span-3" : "min-w-0"}>
              <dt className="text-xs font-medium text-muted-foreground">{f.label}</dt>
              <dd
                className={`mt-0.5 text-sm font-medium break-words ${f.mono ? "font-mono text-[13px] break-all" : ""}`}
              >
                {f.value}
              </dd>
            </div>
          ))}
        </dl>

        {authorized && delivery.phase === "waiting_files" && (
          <Alert tone="info">
            A nota está autorizada; o DANFE e o XML estão sendo obtidos do provedor e aparecem aqui
            em instantes.
          </Alert>
        )}
        {authorized && delivery.phase === "working" && (
          <p role="status" className="text-sm text-muted-foreground">
            Abrindo o DANFE e baixando os arquivos…
          </p>
        )}
        {authorized && delivery.phase === "done" && (
          <div className="space-y-2">
            {delivery.opened === true && (
              <p className="flex items-center gap-2 text-sm text-success-strong">
                <Icon name="check" size={16} /> DANFE aberto em nova guia.
              </p>
            )}
            {delivery.opened === false && (
              <Alert tone="warn">
                O DANFE não abriu sozinho (o navegador bloqueou a guia ou o arquivo não é PDF). Use{" "}
                <strong>Visualizar DANFE</strong> abaixo.
              </Alert>
            )}
            {delivery.downloaded && (
              <p className="text-sm text-muted-foreground">
                Download automático iniciado: <strong>{delivery.downloaded}</strong> (DANFE e XML
                num arquivo só). Os botões abaixo baixam cada arquivo de novo.
              </p>
            )}
          </div>
        )}
        {delivery.phase === "error" && (
          <Alert tone="warn">
            Não foi possível abrir ou baixar os arquivos automaticamente ({delivery.message}). Use
            os botões abaixo — a nota continua autorizada.
          </Alert>
        )}

        {authorized && danfe && <FiscalFileLinks doc={doc} />}
        {authorized && <PrintActions doc={doc} fileButtons={false} />}

        {(stage.key === "parada" ||
          stage.key === "falha_comunicacao" ||
          stage.key === "nova_tentativa" ||
          stage.key === "sem_worker") && (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              onClick={() => consult.mutate({})}
              disabled={consult.isPending}
            >
              Consultar agora
            </Button>
            <span className="text-sm text-muted-foreground">
              Opcional: a consulta já acontece sozinha. Nunca reenvia nem cria outra nota.
            </span>
            {consult.isSuccess && <StatusBadge tone="info">Consulta agendada</StatusBadge>}
          </div>
        )}
        {consult.error !== null && <Alert tone="error">{errorMessage(consult.error)}</Alert>}
      </div>
    </section>
  );
}
